import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ComposedChart,
  Line,
} from "recharts";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const MetricsTab = ({ project }) => {
  const [activeSubTab, setActiveSubTab] = useState("finances");
  const [metricsData, setMetricsData] = useState({
    revenueChartData: null,
    timesheetChartData: null,
    billsChartData: null,
    pieceworkerChartData: null,
    contractChartData: null,
    estimatedCostChartData: null,
    tableData: [],
  });
  const [financeData, setFinanceData] = useState({
    originalContractValue: 0,
    changeOrderValue: 0,
    totalEstimatedCost: 0,
    originalEstimatedCost: 0,
    changeOrderImpact: 0,
    tAndMCost: 0,
    varianceImpact: 0,
    purchaseOrders: [],
    workOrders: [],
    totalWsibCost: 0,
    totalBurdenCost: 0,
    totalPieceworkerBurdenCost: 0,
    totalWorkOrderActualSubtotal: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Colors for charts
  const COLORS = [
    "#4CAF50", // Green
    "#2196F3", // Blue
    "#F44336", // Red
    "#FF9800", // Orange
    "#3F51B5", // Indigo
    "#9C27B0", // Purple
    "#FF5722", // Deep Orange
  ];

  // Custom Tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-700 p-3 border border-gray-600 rounded shadow-md">
          <p className="text-sm font-semibold text-gray-200">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-xs text-gray-300">
              {entry.name}:{" "}
              {entry.dataKey.includes("Cost") ||
              entry.dataKey.includes("Invoiced") ||
              entry.dataKey.includes("Orders") ||
              entry.dataKey.includes("Holdback")
                ? `$${entry.value.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                  })}`
                : `$${entry.value.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                  })}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const calculateCoverageFromSize = (size, uom) => {
    if (!size) return 1;
    const uomLower = (uom || "").toLowerCase();
    const isSF =
      uomLower.includes("sf") ||
      uomLower.includes("square") ||
      uomLower.includes("sq");
    const parseToFeet = (str) => {
      const s = String(str).trim();
      const match = s.match(/^([\d.]+)'?\s*([\d.]+)"?$/);
      if (match) {
        return parseFloat(match[1]) + parseFloat(match[2] || 0) / 12;
      }
      if (s.endsWith("'")) return parseFloat(s.slice(0, -1)) || 0;
      if (s.endsWith('"')) return (parseFloat(s.slice(0, -1)) || 0) / 12;
      return parseFloat(s) || 0;
    };
    if (isSF) {
      const parts = String(size)
        .replace(/×/g, "x")
        .split(/\s*[xX]\s*/);
      if (parts.length === 2) {
        const width = parseToFeet(parts[0]);
        const height = parseToFeet(parts[1]);
        if (width > 0 && height > 0) {
          return Number((width * height).toFixed(2));
        }
      }
    }
    const length = parseToFeet(size);
    if (length > 0) return Number(length.toFixed(2));
    return 1;
  };
  const getCoverage = (item) => {
    if (!item) return 1;

    // Prefer current size on the MO item (just like MaterialsTab)
    const currentSize = item.size || "";
    const currentUom = item.uom || item.container || "";

    const calculated = calculateCoverageFromSize(currentSize); // ← same function name!

    if (calculated > 1) return calculated;

    // Fallback to original line item coverage if available
    if (
      item.lineItem?.coveragePerUnit !== undefined &&
      item.lineItem?.coveragePerUnit !== null &&
      item.lineItem?.coveragePerUnit !== ""
    ) {
      const parsed = parseFloat(item.lineItem.coveragePerUnit);
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }

    return 1;
  };

  // Function to calculate cost breakdown
  // Function to calculate cost breakdown - now matches BudgetTab logic exactly
  const calculateBreakdown = () => {
    // Original estimated cost: sum of amounts from original (non-variance, non-change-order, non-T&M) budgets
    const originalEstimatedCostBeforeVariance = (project.budgets || [])
      .filter(
        (budget) =>
          !budget.isVariance &&
          !budget.isChangeOrder &&
          !budget.isTimeAndMaterial,
      )
      .flatMap((budget) => budget.lineItems || [])
      .reduce((sum, item) => {
        // Skip new spec or change delta items - they are not part of original estimate
        if (item.isNewSpec || item.isChangeDelta) return sum;

        // For items closed out by size change, use original qty/rate
        if (item.closedOutBySizeChange && item.originalRequiredQty != null) {
          const coverage = parseFloat(item.coveragePerUnit || 1);
          const rate = item.price || item.cost || 0;
          return sum + item.originalRequiredQty * coverage * rate;
        }

        // For modified items, use stored originalQty if available
        if (item.originalQty != null) {
          const coverage = parseFloat(item.coveragePerUnit || 1);
          const rate =
            item.category === "M"
              ? item.price || item.cost || 0
              : item.unitRate || 0;
          return sum + item.originalQty * coverage * rate;
        }

        // Default: use current amount (unmodified items)
        return sum + (item.amount || 0);
      }, 0);

    // T&M Cost

    // Variance Impact: sum of grandTotal from confirmed variance budgets
    const varianceImpact = (project.budgets || [])
      .filter((budget) => budget.isVariance && budget.status === "confirmed")
      .reduce((sum, budget) => sum + (budget.grandTotal || 0), 0);

    // Change Order Impact: sum of line item amounts from change order budgets
    const changeOrderImpact = (project.budgets || [])
      .filter(
        (budget) =>
          !budget.isVariance &&
          budget.isChangeOrder &&
          !budget.isTimeAndMaterial,
      )
      .reduce((sum, budget) => {
        const budgetAmount = (budget.lineItems || []).reduce(
          (acc, item) => acc + (item.amount || 0),
          0,
        );
        return sum + budgetAmount;
      }, 0);

    const tAndMCost = (project.budgets || [])
      .filter((budget) => budget.isChangeOrder && budget.isTimeAndMaterial)
      .reduce((sum, budget) => {
        const budgetAmount = (budget.lineItems || []).reduce(
          (acc, item) => acc + (item.amount || 0),
          0,
        );
        return sum + budgetAmount;
      }, 0);

    const originalEstimatedCost = originalEstimatedCostBeforeVariance;
    const totalEstimatedCost =
      originalEstimatedCostBeforeVariance +
      tAndMCost +
      changeOrderImpact +
      varianceImpact;

    return {
      totalEstimatedCost,
      originalEstimatedCost,
      changeOrderImpact,
      tAndMCost,
      varianceImpact,
    };
  };

  useEffect(() => {
    const fetchMetricsData = async () => {
      try {
        const token = localStorage.getItem("token");
        const projectId = project._id;
        const currentYear = new Date().getFullYear();

        const safeNumber = (val, def = 0) => {
          const parsed = parseFloat(val);
          return isNaN(parsed) ? def : parsed;
        };

        const calculateCoverageFromSize = (size, uom = "") => {
          if (!size || typeof size !== "string") return 1;
          const trimmed = size.trim();
          if (
            !trimmed ||
            trimmed === "" ||
            trimmed === "N/A" ||
            trimmed === "-"
          )
            return 1;

          const parseDimensionToFeet = (dimStr) => {
            const s = dimStr.trim();
            const mixedMatch = s.match(
              /^(\d+(?:\.\d+)?)\s*'?\s*(\d+(?:\.\d+)?)?\s*"?$/i,
            );
            if (mixedMatch) {
              const feet = parseFloat(mixedMatch[1]) || 0;
              const inches = parseFloat(mixedMatch[2] || 0);
              return feet + inches / 12;
            }
            if (s.endsWith("'") && !s.includes('"')) {
              const num = parseFloat(s.slice(0, -1));
              return isNaN(num) ? 0 : num;
            }
            if (s.endsWith('"')) {
              const num = parseFloat(s.slice(0, -1));
              return isNaN(num) ? 0 : num / 12;
            }
            const num = parseFloat(s);
            return !isNaN(num) && num > 0 ? num : 0;
          };

          const normalized = trimmed
            .replace(/×/g, "x")
            .replace(/\s+/g, " ")
            .trim();
          const parts = normalized.split(/\s*[xX]\s*/);

          if (parts.length >= 2) {
            const width = parseDimensionToFeet(parts[0]);
            const height = parseDimensionToFeet(parts[1]);
            if (width > 0 && height > 0) {
              return Number((width * height).toFixed(4));
            }
          }

          const uomLower = (uom || "").toLowerCase();
          if (uomLower.includes("lf") || uomLower.includes("linear")) {
            const length = parseDimensionToFeet(normalized);
            if (length > 0) return Number(length.toFixed(4));
          }

          return 1;
        };

        const getItemCoverage = (item) => {
          // Priority 1: Use ORIGINAL coverage from the budget line item if available (most stable)
          if (
            item.lineItem?.coveragePerUnit != null &&
            item.lineItem?.coveragePerUnit !== "" &&
            !isNaN(parseFloat(item.lineItem.coveragePerUnit))
          ) {
            const originalCov = parseFloat(item.lineItem.coveragePerUnit);
            if (originalCov > 0) {
              return originalCov;
            }
          }

          // Priority 2: Try to use the ORIGINAL required qty / original ordered qty if available
          if (
            item.originalRequiredQty != null &&
            item.originalRequiredQty > 0
          ) {
            const originalQty = parseFloat(item.originalRequiredQty);
            const currentQty = safeNumber(item.quantity, 0);
            if (currentQty > 0) {
              // This helps when someone increased qty but we want original coverage logic
              return originalQty / currentQty; // approximate original coverage factor
            }
          }

          // Priority 3: Calculate from current size (only if nothing better exists)
          const currentSize = item.size || item.originalSize || "";
          const currentUom = item.uom || item.container || item.unit || "";
          const calculated = calculateCoverageFromSize(currentSize, currentUom);

          if (calculated > 0 && calculated !== 1) {
            return Number(calculated.toFixed(4));
          }

          // Ultimate fallback
          return 1;
        };

        const requests = [
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/sovs/project/${projectId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          ),
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/timesheets/project/${projectId}/all`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          ),
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/pieceworkerLogs2?project=${projectId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          ),
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/bills/project/${projectId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          ),
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/company-profile`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          ),
        ];

        let materialOrdersResponse = null;
        let changeOrdersResponse = null;
        let purchaseOrders2Response = null;
        let workOrders2Response = null;

        if (project.version === 2) {
          requests.push(
            axios.get(
              `https://erp-production-72da01c8e651.herokuapp.com/api/changeOrders2/project/${projectId}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              },
            ),
            axios.get(
              `https://erp-production-72da01c8e651.herokuapp.com/api/purchaseOrders2/project/${projectId}/all-summaries`,
              { headers: { Authorization: `Bearer ${token}` } },
            ),
            axios.get(
              `https://erp-production-72da01c8e651.herokuapp.com/api/workOrders2/project/${projectId}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              },
            ),
            axios.get(
              `https://erp-production-72da01c8e651.herokuapp.com/api/materialOrders2/project/${projectId}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              },
            ),
          );
        } else {
          requests.push(
            axios.get(
              `https://erp-production-72da01c8e651.herokuapp.com/api/materialOrders/project/${projectId}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              },
            ),
          );
        }

        const responses = await Promise.all(requests);

        let idx = 0;
        const sovResponse = responses[idx++];
        const timesheetResponse = responses[idx++];
        const pieceworkerResponse = responses[idx++];
        const billsResponse = responses[idx++];
        const companyProfileResponse = responses[idx++];

        if (project.version === 2) {
          changeOrdersResponse = responses[idx++];
          purchaseOrders2Response = responses[idx++];
          workOrders2Response = responses[idx++];
          materialOrdersResponse = responses[idx++];
        } else {
          materialOrdersResponse = responses[idx++];
        }

        const companyProfile = companyProfileResponse.data;
        const materialOrders = materialOrdersResponse.data || [];
        const bills = billsResponse.data || [];
        const timesheets = timesheetResponse.data.timesheets || [];
        const pieceworkerLogs = pieceworkerResponse.data.data || [];

        console.log(pieceworkerLogs);

        const purchaseOrdersData =
          project.version === 2
            ? purchaseOrders2Response?.data?.purchaseOrders || []
            : project.purchaseOrders || [];

        const workOrdersData =
          project.version === 2
            ? workOrders2Response?.data?.workOrders || []
            : project.workOrders || [];

        const months = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const totalInvoiced = new Array(12).fill(0);
        const totalCosts = new Array(12).fill(0);
        const timesheetApproved = new Array(12).fill(0);
        const timesheetPending = new Array(12).fill(0);
        const billsPaid = new Array(12).fill(0);
        const billsUnpaid = new Array(12).fill(0);
        const pieceworkerApproved = new Array(12).fill(0);
        const pieceworkerPending = new Array(12).fill(0);

        let totalInvoicedSum = 0;
        let totalCompletedToDateSum = 0;
        let withHoldback = false;

        (sovResponse.data.scheduleOfValues || []).forEach((sov) => {
          const date = new Date(sov.createdAt);
          totalInvoicedSum += parseFloat(sov.totalInvoiced || 0);
          sov.items.forEach((item) => {
            totalCompletedToDateSum += parseFloat(
              item.totalCompletedToDate || 0,
            );
          });
          if (sov.withHoldback) withHoldback = true;
          if (date.getFullYear() === currentYear) {
            totalInvoiced[date.getMonth()] += parseFloat(
              sov.totalInvoiced || 0,
            );
          }
        });

        const companyTaxRate = parseFloat(
          companyProfile?.defaultTax?.taxRate || 0,
        );
        const totalProjectHoldback = withHoldback
          ? (
              totalCompletedToDateSum *
              (1 + companyTaxRate / 100) *
              0.1
            ).toFixed(2)
          : "0.00";

        (timesheetResponse.data.timesheets || []).forEach((ts) => {
          const date = new Date(ts.start);
          if (date.getFullYear() === currentYear && ts.totalCost) {
            const monthIdx = date.getMonth();
            totalCosts[monthIdx] += parseFloat(ts.totalCost || 0);
            if (ts.status === true && ts.Rejected === false) {
              timesheetApproved[monthIdx] += parseFloat(ts.totalCost || 0);
            } else if (!ts.status || ts.clockInStatus || !ts.end) {
              timesheetPending[monthIdx] += parseFloat(ts.totalCost || 0);
            }
          }
        });

        (pieceworkerResponse.data.data || []).forEach((pw) => {
          const date = new Date(pw.submittedAt || pw.workCompletedDate);
          if (date.getFullYear() === currentYear && pw.totalCost) {
            const monthIdx = date.getMonth();
            totalCosts[monthIdx] += parseFloat(pw.totalCost || 0);
            if (pw.status === "approved") {
              pieceworkerApproved[monthIdx] += parseFloat(pw.totalCost || 0);
            } else if (pw.status === "pending") {
              pieceworkerPending[monthIdx] += parseFloat(pw.totalCost || 0);
            }
          }
        });

        (billsResponse.data || []).forEach((bill) => {
          const date = new Date(bill.invoiceDate);
          if (date.getFullYear() === currentYear && bill.subtotal) {
            const monthIdx = date.getMonth();
            totalCosts[monthIdx] += parseFloat(bill.subtotal || 0);
            if (
              bill.processingStatus === "processed" &&
              (bill.invoiceStatus === "paid" || bill.datePaid)
            ) {
              billsPaid[monthIdx] += parseFloat(bill.subtotal || 0);
            } else {
              billsUnpaid[monthIdx] += parseFloat(bill.subtotal || 0);
            }
          }
        });

        const gpm = totalInvoiced.map((invoiced, idx) => {
          const costs = totalCosts[idx];
          return invoiced > 0 ? ((invoiced - costs) / invoiced) * 100 : 0;
        });

        const revenueChartData = months.map((month, idx) => ({
          month,
          "Total Invoiced": totalInvoiced[idx],
          "Gross Profit Margin": gpm[idx],
          "GPM Goal": 30,
        }));

        const timesheetChartData = months.map((month, idx) => ({
          month,
          Approved: timesheetApproved[idx],
          Pending: timesheetPending[idx],
        }));

        const billsChartData = months.map((month, idx) => ({
          month,
          Paid: billsPaid[idx],
          Unpaid: billsUnpaid[idx],
        }));

        const pieceworkerChartData = months.map((month, idx) => ({
          month,
          Approved: pieceworkerApproved[idx],
          Pending: pieceworkerPending[idx],
        }));

        let changeOrderValue = project.changeOrderContractValue || 0;
        if (project.version === 2 && changeOrdersResponse?.data?.changeOrders) {
          changeOrderValue = changeOrdersResponse.data.changeOrders
            .filter((co) => co.approvalStatus === "Approved")
            .reduce((sum, co) => sum + (co.totalCustomerPrice || 0), 0);
        }

        const originalContractValue = project.originalContractValue || 0;

        const contractChartData = [
          {
            name: "Total Invoiced",
            "Total Invoiced": totalInvoicedSum,
            "Total Holdback": parseFloat(totalProjectHoldback),
          },
          {
            name: "Contract Value",
            "Original Contract": originalContractValue,
            "Change Orders": changeOrderValue,
          },
        ];

        const {
          totalEstimatedCost,
          originalEstimatedCost,
          changeOrderImpact,
          tAndMCost,
          varianceImpact,
        } = calculateBreakdown();

        const purchaseOrders = purchaseOrdersData.map((po) => {
          const estimatedCost = po.estimatedTotal || 0;

          // === VERSION-AWARE BILL FILTERING FOR PO ===
          const poBills = bills.filter((bill) => {
            if (project.version === 2) {
              return (
                bill.relatedPO2 &&
                (bill.relatedPO2._id?.toString() === po._id.toString() ||
                  bill.relatedPO2?.toString() === po._id.toString())
              );
            } else {
              return (
                bill.relatedPO &&
                (bill.relatedPO._id?.toString() === po._id.toString() ||
                  bill.relatedPO?.toString() === po._id.toString())
              );
            }
          });

          // === VERSION-AWARE MATERIAL ORDER FILTERING FOR PO ===
          // Inside purchaseOrders.map(...)
          const poMaterialOrders = materialOrders.filter((mo) => {
            const moPOId = mo.purchaseOrder?._id || mo.purchaseOrder;
            return (
              moPOId?.toString() === po._id.toString() &&
              [
                "pending",
                "delivered",
                "confirmed",
                "partially_delivered",
              ].includes(mo.status)
            );
          });

          const pendingMOCost = poMaterialOrders.reduce((totalPending, mo) => {
            // ── Same core calculation as MaterialsTab ──────────────────────────────
            const itemsTotal = (mo.items || []).reduce((sum, item) => {
              const quantity = safeNumber(item.quantity, 0);
              const price = safeNumber(item.price || item.unitPrice, 0);

              // Use the same coverage logic as MaterialsTab
              const coverage = getCoverage(item); // ← Must be same function name/logic!

              return sum + quantity * coverage * price;
            }, 0);

            const surchargesTotal = (mo.surcharges || []).reduce(
              (sum, s) => sum + safeNumber(s.amount || s.price || s.value, 0),
              0,
            );

            const moTheoreticalTotal = itemsTotal + surchargesTotal;
            // ───────────────────────────────────────────────────────────────────────

            // Find bills related to this exact material order
            const moBills = bills.filter((bill) => {
              if (project.version === 2) {
                return (
                  bill.relatedMO2 &&
                  (bill.relatedMO2._id?.toString() === mo._id.toString() ||
                    bill.relatedMO2?.toString() === mo._id)
                );
              }
              return (
                bill.relatedMO &&
                (bill.relatedMO._id?.toString() === mo._id.toString() ||
                  bill.relatedMO?.toString() === mo._id)
              );
            });

            // Sum only processed bills (what has already been billed)
            const billedSoFar = moBills
              .filter((b) => b.processingStatus === "processed")
              .reduce((sum, bill) => {
                const value = bill.subtotal
                  ? safeNumber(bill.subtotal)
                  : safeNumber(bill.total || 0) /
                    (1 + (companyTaxRate / 100 || 0));
                return sum + value;
              }, 0);

            // Remaining to be billed for this MO
            const pendingThisMO = Math.max(0, moTheoreticalTotal - billedSoFar);

            return totalPending + pendingThisMO;
          }, 0);

          // === ACTUAL COST FROM BILLS ===
          const actualCost = poBills
            .filter((b) => b.processingStatus === "processed")
            .reduce((sum, bill) => {
              const subtotal = bill.subtotal
                ? parseFloat(bill.subtotal)
                : parseFloat(bill.total || 0) / (1 + companyTaxRate / 100);
              return sum + subtotal;
            }, 0);

          // === PENDING BILLS (not tied to MO) ===
          const pendingBillCost = poBills
            .filter(
              (b) =>
                b.processingStatus === "pending" &&
                !b.relatedMO &&
                !b.relatedMO2,
            )
            .reduce((sum, bill) => {
              const subtotal = bill.subtotal
                ? parseFloat(bill.subtotal)
                : parseFloat(bill.total || 0) / (1 + companyTaxRate / 100);
              return sum + subtotal;
            }, 0);

          const totalForecasted = actualCost + pendingMOCost + pendingBillCost;
          const remainingCost = estimatedCost - actualCost - pendingMOCost;

          return {
            poId: po._id,
            title: po.title,
            poNumber: po.poNumber || "N/A",
            estimatedCost,
            actualCost,
            pendingMOCost,
            pendingBillCost,
            totalForecasted,
            remainingCost,
          };
        });

        const workOrders = workOrdersData.map((wo) => {
          const estimatedCost = wo.estimatedTotal || 0;

          const woBills = bills.filter((bill) => {
            if (project.version === 2) {
              return (
                bill.relatedWO2 &&
                (bill.relatedWO2._id?.toString() === wo._id.toString() ||
                  bill.relatedWO2?.toString() === wo._id.toString())
              );
            } else {
              return (
                bill.relatedWO &&
                (bill.relatedWO._id?.toString() === wo._id.toString() ||
                  bill.relatedWO?.toString() === wo._id.toString())
              );
            }
          });

          const billsActualCost = woBills
            .filter((b) => b.processingStatus === "processed")
            .reduce((sum, bill) => {
              const subtotal = bill.subtotal
                ? parseFloat(bill.subtotal)
                : parseFloat(bill.total || 0) / (1 + companyTaxRate / 100);
              return sum + subtotal;
            }, 0);

          const billsPendingCost = woBills
            .filter((b) => b.processingStatus === "pending")
            .reduce((sum, bill) => {
              const subtotal = bill.subtotal
                ? parseFloat(bill.subtotal)
                : parseFloat(bill.total || 0) / (1 + companyTaxRate / 100);
              return sum + subtotal;
            }, 0);

          const woPieceworkerLogs = pieceworkerLogs.filter(
            (pw) =>
              pw.workOrder &&
              (pw.workOrder._id?.toString() === wo._id.toString() ||
                pw.workOrder?.toString() === wo._id.toString()),
          );

          const pieceworkerActualCostBreakdown = woPieceworkerLogs
            .filter((pw) => pw.status === "approved")
            .reduce(
              (acc, pw) => {
                const subtotal = (pw.lineItems || []).reduce(
                  (s, item) => s + parseFloat(item.subtotal || 0),
                  0,
                );
                const wsibCost = (pw.lineItems || []).reduce(
                  (s, item) => s + parseFloat(item.wsibCost || 0),
                  0,
                );
                const pieceworkerBurdenCost = (pw.lineItems || []).reduce(
                  (s, item) => s + parseFloat(item.pieceworkerBurdenCost || 0),
                  0,
                );
                return {
                  subtotal: acc.subtotal + subtotal,
                  wsibCost: acc.wsibCost + wsibCost,
                  pieceworkerBurdenCost:
                    acc.pieceworkerBurdenCost + pieceworkerBurdenCost,
                  total: acc.total + parseFloat(pw.totalCost || 0),
                };
              },
              { subtotal: 0, wsibCost: 0, pieceworkerBurdenCost: 0, total: 0 },
            );

          const pieceworkerPendingCostBreakdown = woPieceworkerLogs
            .filter((pw) => pw.status === "pending")
            .reduce(
              (acc, pw) => {
                const subtotal = (pw.lineItems || []).reduce(
                  (s, item) => s + parseFloat(item.subtotal || 0),
                  0,
                );
                const wsibCost = (pw.lineItems || []).reduce(
                  (s, item) => s + parseFloat(item.wsibCost || 0),
                  0,
                );
                const pieceworkerBurdenCost = (pw.lineItems || []).reduce(
                  (s, item) => s + parseFloat(item.pieceworkerBurdenCost || 0),
                  0,
                );
                return {
                  subtotal: acc.subtotal + subtotal,
                  wsibCost: acc.wsibCost + wsibCost,
                  pieceworkerBurdenCost:
                    acc.pieceworkerBurdenCost + pieceworkerBurdenCost,
                  total: acc.total + parseFloat(pw.totalCost || 0),
                };
              },
              { subtotal: 0, wsibCost: 0, pieceworkerBurdenCost: 0, total: 0 },
            );

          // Improved filtering for v2 compatibility
          const woTimesheets = timesheets.filter((ts) => {
            const woId = wo._id.toString();
            return (
              ts.workOrderId?.toString() === woId ||
              ts.workOrderRaw?._id?.toString() === woId ||
              ts.workOrder2Raw?._id?.toString() === woId
            );
          });

          const timesheetActualCostBreakdown = woTimesheets
            .filter((ts) => ts.status === true && ts.Rejected === false)
            .reduce(
              (acc, ts) => ({
                subtotal: acc.subtotal + safeNumber(ts.subtotal),
                wsibCost: acc.wsibCost + safeNumber(ts.wsibCost),
                burdenCost: acc.burdenCost + safeNumber(ts.burdenCost),
                total: acc.total + safeNumber(ts.totalCost),
              }),
              { subtotal: 0, wsibCost: 0, burdenCost: 0, total: 0 },
            );

          // In the workOrders.map loop, update the pending breakdown:
          // Inside workOrders.map → timesheetPendingCostBreakdown
          const timesheetPendingCostBreakdown = woTimesheets
            .filter(
              (ts) =>
                ts.status === false && // Not approved
                ts.Rejected === false, // Not rejected
              // DO NOT add extra conditions like clockInStatus or !end here
              // Approved ones should already be excluded by status === true
            )
            .reduce(
              (acc, ts) => ({
                subtotal: acc.subtotal + safeNumber(ts.subtotal),
                wsibCost: acc.wsibCost + safeNumber(ts.wsibCost),
                burdenCost: acc.burdenCost + safeNumber(ts.burdenCost),
                total: acc.total + safeNumber(ts.totalCost),
              }),
              { subtotal: 0, wsibCost: 0, burdenCost: 0, total: 0 },
            );

          console.log(timesheetPendingCostBreakdown);

          const actualCostBreakdown = {
            subtotal:
              billsActualCost +
              timesheetActualCostBreakdown.subtotal +
              pieceworkerActualCostBreakdown.subtotal,
            wsibCost:
              timesheetActualCostBreakdown.wsibCost +
              pieceworkerActualCostBreakdown.wsibCost,
            burdenCost: timesheetActualCostBreakdown.burdenCost,
            pieceworkerBurdenCost:
              pieceworkerActualCostBreakdown.pieceworkerBurdenCost,
            total:
              billsActualCost +
              timesheetActualCostBreakdown.total +
              pieceworkerActualCostBreakdown.total,
          };

          const pendingCostBreakdown = {
            subtotal:
              billsPendingCost +
              timesheetPendingCostBreakdown.subtotal +
              pieceworkerPendingCostBreakdown.subtotal,
            wsibCost:
              timesheetPendingCostBreakdown.wsibCost +
              pieceworkerPendingCostBreakdown.wsibCost,
            burdenCost: timesheetPendingCostBreakdown.burdenCost,
            pieceworkerBurdenCost:
              pieceworkerPendingCostBreakdown.pieceworkerBurdenCost,
            total:
              billsPendingCost +
              timesheetPendingCostBreakdown.total +
              pieceworkerPendingCostBreakdown.total,
          };

          const actualCost = actualCostBreakdown.total;
          const pendingCost = pendingCostBreakdown.total;
          const remainingCost = estimatedCost - actualCost - pendingCost;

          return {
            woId: wo._id,
            title: wo.title,
            woNumber: wo.woNumber || "N/A",
            estimatedCost,
            actualCost,
            actualCostBreakdown,
            pendingCost,
            pendingCostBreakdown,
            remainingCost,
          };
        });

        const totalWorkOrderActualSubtotal = workOrders.reduce(
          (sum, wo) => sum + (wo.actualCostBreakdown.subtotal || 0),
          0,
        );

        const totalWsibCost = workOrders.reduce(
          (sum, wo) => sum + (wo.actualCostBreakdown.wsibCost || 0),
          0,
        );

        const totalBurdenCost = workOrders.reduce(
          (sum, wo) => sum + (wo.actualCostBreakdown.burdenCost || 0),
          0,
        );

        const totalPieceworkerBurdenCost = workOrders.reduce(
          (sum, wo) =>
            sum + (wo.actualCostBreakdown.pieceworkerBurdenCost || 0),
          0,
        );

        const totalActualCost =
          purchaseOrders.reduce((sum, po) => sum + (po.actualCost || 0), 0) +
          workOrders.reduce((sum, wo) => sum + (wo.actualCost || 0), 0);

        const totalForecastedCost =
          purchaseOrders.reduce(
            (sum, po) =>
              sum +
              (po.actualCost || 0) +
              (po.pendingMOCost || 0) +
              (po.pendingBillCost || 0),
            0,
          ) +
          workOrders.reduce(
            (sum, wo) => sum + (wo.actualCost || 0) + (wo.pendingCost || 0),
            0,
          );

        const estimatedCostChartData = [
          {
            name: "Estimated Cost",
            "Original Estimated Cost": originalEstimatedCost,
            "T&M Cost": tAndMCost,
            "Change Order Impact": changeOrderImpact,
            "Variance Impact": varianceImpact,
          },
          {
            name: "Actual Cost",
            "Work Order Actual Cost": workOrders.reduce(
              (sum, wo) => sum + (wo.actualCost || 0),
              0,
            ),
            "Purchase Order Actual Cost": purchaseOrders.reduce(
              (sum, po) => sum + (po.actualCost || 0),
              0,
            ),
            "Pending Costs": totalForecastedCost - totalActualCost,
          },
        ];

        const tableData = months.map((month, idx) => ({
          month,
          totalInvoiced: totalInvoiced[idx].toFixed(2),
          totalCosts: totalCosts[idx].toFixed(2),
          gpm: gpm[idx].toFixed(2),
        }));

        setMetricsData({
          revenueChartData,
          timesheetChartData,
          billsChartData,
          pieceworkerChartData,
          contractChartData,
          estimatedCostChartData,
          tableData,
        });

        setFinanceData({
          originalContractValue,
          changeOrderValue,
          totalEstimatedCost,
          originalEstimatedCost,
          changeOrderImpact,
          tAndMCost,
          varianceImpact,
          purchaseOrders,
          workOrders,
          totalWsibCost,
          totalBurdenCost,
          totalPieceworkerBurdenCost,
          totalWorkOrderActualSubtotal,
        });

        setLoading(false);
      } catch (err) {
        console.error("Error fetching metrics:", err);
        setError(
          "Failed to load metrics data: " +
            (err.response?.data?.message || err.message),
        );
        setLoading(false);
      }
    };

    fetchMetricsData();
  }, [project]);

  const currentYear = new Date().getFullYear();

  const formatNumber = (num) =>
    (num ?? 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  if (loading)
    return (
      <div className="text-center py-4 text-gray-200">Loading metrics...</div>
    );
  if (error)
    return <div className="text-red-400 text-center py-4">{error}</div>;

  return (
    <div className="bg-gray-200 rounded-lg ">
      <div className="mb-4">
        <nav className="flex space-x-4" aria-label="Tabs">
          <button
            onClick={() => setActiveSubTab("finances")}
            className={classNames(
              activeSubTab === "finances"
                ? "bg-orange-500 text-white font-semibold"
                : "text-gray-500 hover:text-gray-700",
              "rounded-md px-3 py-2 text-sm font-medium",
            )}
          >
            Finances
          </button>
          <button
            onClick={() => setActiveSubTab("Metrics")}
            className={classNames(
              activeSubTab === "Metrics"
                ? "bg-orange-500 text-white font-semibold"
                : "text-gray-500 hover:text-gray-700",
              "rounded-md px-3 py-2 text-sm font-medium",
            )}
          >
            Metrics
          </button>
        </nav>
      </div>

      {activeSubTab === "Metrics" && (
        <div className="grid grid-cols-1 gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className="bg-gray-700 p-4 rounded-lg shadow"
              style={{ height: "400px" }}
            >
              <h3 className="text-md font-semibold text-gray-200 mb-4">
                Revenue and GPM ({currentYear})
              </h3>
              {console.log(metricsData.revenueChartData)}
              {metricsData.revenueChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart
                    data={metricsData.revenueChartData.map((item) => ({
                      ...item,
                    }))}
                    margin={{ top: 20, right: 20, bottom: 100, left: 20 }}
                  >
                    <XAxis
                      dataKey="month"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={60}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                    />
                    <YAxis
                      yAxisId="left"
                      label={{
                        value: "Revenue ($)",
                        angle: -90,
                        position: "insideLeft",
                        fill: "#e5e7eb",
                        fontSize: 12,
                        dx: -10, // Moves label left
                      }}
                      padding={{ top: 20 }}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                      tickFormatter={(value) => value.toLocaleString("en-US")}
                    />
                    <YAxis
                      yAxisId="right"
                      padding={{ top: 20 }}
                      orientation="right"
                      label={{
                        value: "GPM (%)",
                        angle: -90,
                        position: "insideRight",
                        fill: "#e5e7eb",
                        fontSize: 12,
                      }}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                      tickFormatter={(value) => value.toLocaleString("en-US")}
                      domain={[0, 100]} // Focus on 0% to 30% range
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      align="center"
                      wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="Total Invoiced"
                      name="Total Invoiced ($)"
                      fill={COLORS[1] || "#82ca9d"} // Greenish fill for bars
                      radius={[12, 12, 0, 0]} // Rounded top corners
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="Gross Profit Margin"
                      name="Gross Profit Margin (%)"
                      stroke={COLORS[2] || "#ffc658"} // Yellow for visibility
                      strokeWidth={2}
                      dot={{ r: 4, stroke: COLORS[2] || "#ffc658" }} // Visible dots
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="GPM Goal"
                      name="GPM Goal (30%)"
                      stroke={COLORS[0] || "#FF0000"} // Red for visibility
                      strokeDasharray="5 5" // Dashed line
                      strokeWidth={3}
                      dot={{ r: 4, stroke: COLORS[0] || "#FF0000" }} // Visible dots
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-400 text-center text-sm">
                  No revenue data available
                </p>
              )}
            </div>
            <div
              className="bg-gray-700 p-4 rounded-lg shadow"
              style={{ height: "400px" }}
            >
              <h3 className="text-md font-semibold text-gray-200 mb-4">
                Timesheet Costs ({currentYear})
              </h3>
              {metricsData.timesheetChartData &&
              metricsData.timesheetChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={metricsData.timesheetChartData}
                    margin={{ top: 20, right: 20, bottom: 100, left: 20 }}
                  >
                    <XAxis
                      dataKey="month"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={60}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                    />
                    <YAxis
                      label={{
                        value: "Costs ($)",
                        angle: -90,
                        position: "insideLeft",
                        fill: "#e5e7eb",

                        fontSize: 12,
                      }}
                      padding={{ top: 20 }}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                      tickFormatter={(value) => value.toLocaleString("en-US")}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      align="center"
                      wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                    />
                    <Bar
                      dataKey="Approved"
                      name="Approved"
                      fill={COLORS[0]}
                      stackId="timesheet"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Pending"
                      name="Pending"
                      fill={COLORS[3]}
                      stackId="timesheet"
                      radius={[12, 12, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-400 text-center text-sm">
                  No timesheet data available
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className="bg-gray-700 p-4 rounded-lg shadow"
              style={{ height: "400px" }}
            >
              <h3 className="text-md font-semibold text-gray-200 mb-4">
                Bills Costs ({currentYear})
              </h3>
              {metricsData.billsChartData &&
              metricsData.billsChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={metricsData.billsChartData}
                    margin={{ top: 20, right: 20, bottom: 100, left: 20 }}
                  >
                    <XAxis
                      dataKey="month"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={60}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                    />
                    <YAxis
                      label={{
                        value: "Costs ($)",
                        angle: -90,
                        position: "insideLeft",
                        fill: "#e5e7eb",
                        fontSize: 12,
                      }}
                      padding={{ top: 20 }}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                      tickFormatter={(value) => value.toLocaleString("en-US")}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      align="center"
                      wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                    />
                    <Bar
                      dataKey="Paid"
                      name="Paid"
                      fill={COLORS[0]}
                      stackId="bills"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Unpaid"
                      name="Unpaid"
                      fill={COLORS[3]}
                      stackId="bills"
                      radius={[12, 12, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-400 text-center text-sm">
                  No bills data available
                </p>
              )}
            </div>
            <div
              className="bg-gray-700 p-4 rounded-lg shadow"
              style={{ height: "400px" }}
            >
              <h3 className="text-md font-semibold text-gray-200 mb-4">
                Pieceworker Costs ({currentYear})
              </h3>
              {metricsData.pieceworkerChartData &&
              metricsData.pieceworkerChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={metricsData.pieceworkerChartData}
                    margin={{ top: 20, right: 20, bottom: 100, left: 20 }}
                  >
                    <XAxis
                      dataKey="month"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={60}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                    />
                    <YAxis
                      label={{
                        value: "Costs ($)",
                        angle: -90,
                        position: "insideLeft",
                        fill: "#e5e7eb",
                        fontSize: 12,
                      }}
                      padding={{ top: 20 }}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                      tickFormatter={(value) => value.toLocaleString("en-US")}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      align="center"
                      wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                    />
                    <Bar
                      dataKey="Approved"
                      name="Approved"
                      fill={COLORS[0]}
                      stackId="pieceworker"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Pending"
                      name="Pending"
                      fill={COLORS[3]}
                      stackId="pieceworker"
                      radius={[12, 12, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-400 text-center text-sm">
                  No pieceworker data available
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-12">
            <div
              className="bg-gray-700 p-4 rounded-lg shadow"
              style={{ height: "400px" }}
            >
              <h3 className="text-md font-semibold text-gray-200">
                Total Invoiced vs. Contract
              </h3>
              {metricsData.contractChartData &&
              metricsData.contractChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={500}>
                  <BarChart
                    data={metricsData.contractChartData}
                    margin={{
                      top: 50,
                      right: 40,
                      bottom: 150,
                      left: 40,
                    }}
                  >
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={60}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                    />
                    <YAxis
                      label={{
                        value: "Amount ($)",
                        angle: -90,
                        offset: -15,
                        position: "insideLeft",
                        fill: "#e5e7eb",
                        fontSize: 16,
                      }}
                      padding={{ top: 40 }}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                      tickFormatter={(value) => value.toLocaleString("en-US")}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      align="center"
                      wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                    />
                    <Bar
                      dataKey="Total Invoiced"
                      name="Total Invoiced"
                      fill={COLORS[1]}
                      stackId="invoiced"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Total Holdback"
                      name="Total Holdback ($)"
                      fill={COLORS[3]}
                      stackId="invoiced"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Original Contract"
                      name="Original Contract"
                      fill={COLORS[0]}
                      stackId="contract"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Change Orders"
                      name="Change Orders"
                      fill={COLORS[2]}
                      stackId="contract"
                      radius={[12, 12, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-400 text-center text-sm">
                  No contract data available
                </p>
              )}
            </div>
            <div
              className="bg-gray-700 p-4 rounded-lg shadow"
              style={{ height: "400px" }}
            >
              <h3 className="text-md font-semibold text-gray-200 mb-4">
                Estimated vs. Actual Cost Breakdown
              </h3>
              {metricsData.estimatedCostChartData &&
              metricsData.estimatedCostChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={500}>
                  <BarChart
                    data={metricsData.estimatedCostChartData}
                    margin={{ top: 20, right: 20, bottom: 175, left: 20 }}
                  >
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      interval={0}
                      height={60}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                    />
                    <YAxis
                      label={{
                        value: "Amount",
                        angle: -90,
                        position: "insideLeft",
                        fill: "#e5e7eb",
                        offset: -5,
                        fontSize: 16,
                      }}
                      padding={{ top: 40 }}
                      className="text-sm"
                      stroke="#e5e7eb"
                      fontSize={12}
                      tickFormatter={(value) => value.toLocaleString("en-US")}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      align="center"
                      wrapperStyle={{ fontSize: "12px", paddingTop: "20px" }}
                    />
                    <Bar
                      dataKey="Original Estimated Cost"
                      name="Original Estimated Cost"
                      fill={COLORS[0]}
                      stackId="estimated"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="T&M Cost"
                      name="T&M Cost"
                      fill={COLORS[1]}
                      stackId="estimated"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Change Order Impact"
                      name="Change Order Impact"
                      fill={COLORS[3]}
                      stackId="estimated"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Variance Impact"
                      name="Variance Impact"
                      fill={COLORS[5]}
                      stackId="estimated"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Work Order Actual Cost"
                      name="Work Order Actual Cost"
                      fill={COLORS[4]}
                      stackId="actual"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Purchase Order Actual Cost"
                      name="Purchase Order Actual Cost"
                      fill={COLORS[2]}
                      stackId="actual"
                      radius={[12, 12, 0, 0]}
                    />
                    <Bar
                      dataKey="Pending Costs"
                      name="Pending Costs"
                      fill={COLORS[6]}
                      stackId="actual"
                      radius={[12, 12, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-gray-400 text-center text-sm">
                  No cost data available
                </p>
              )}
            </div>
          </div>
          <div className="bg-gray-700 p-4 rounded-lg shadow overflow-auto">
            <h3 className="text-md font-semibold text-gray-200 mb-4">
              Monthly Summary
            </h3>
            <table className="min-w-full divide-y divide-gray-600">
              <thead className="bg-gray-800 sticky top-0 z-10">
                <tr>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Month
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Invoiced ($)
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Costs ($)
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    GPM (%)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-600">
                {metricsData.tableData.map((row, idx) => (
                  <tr key={idx}>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-200">
                      {row.month}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-200">
                      {Number(row.totalInvoiced).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-200">
                      {Number(row.totalCosts).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-200">
                      {row.gpm}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeSubTab === "finances" && (
        <div>
          <div className="bg-gray-700 p-5 rounded-lg shadow mb-6">
            <h3 className="text-lg font-semibold text-gray-100 mb-4 border-b border-gray-600 pb-2">
              Contract Summary
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Contract Value */}
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                  Current Contract Value
                </p>
                <p className="text-xl font-bold text-green-400 mb-3">
                  $
                  {formatNumber(
                    financeData.originalContractValue +
                      financeData.changeOrderValue,
                  )}
                </p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Original Contract</span>
                    <span className="text-gray-300">
                      ${formatNumber(financeData.originalContractValue)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Change Orders</span>
                    <span className="text-gray-300">
                      ${formatNumber(financeData.changeOrderValue)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Estimated Cost */}
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                  Total Estimated Cost
                </p>
                <p className="text-xl font-bold text-blue-400 mb-3">
                  ${formatNumber(financeData.totalEstimatedCost)}
                </p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Original Estimated</span>
                    <span className="text-gray-300">
                      ${formatNumber(financeData.originalEstimatedCost)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">T&M Cost</span>
                    <span className="text-gray-300">
                      ${formatNumber(financeData.tAndMCost)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Change Order Impact</span>
                    <span className="text-gray-300">
                      ${formatNumber(financeData.changeOrderImpact)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Variance Impact</span>
                    <span className="text-gray-300">
                      ${formatNumber(financeData.varianceImpact)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actual Cost */}
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                  Total Actual Cost
                </p>
                <p className="text-xl font-bold text-emerald-400 mb-3">
                  $
                  {formatNumber(
                    financeData.purchaseOrders.reduce(
                      (sum, po) => sum + (po.actualCost || 0),
                      0,
                    ) +
                      financeData.workOrders.reduce(
                        (sum, wo) => sum + (wo.actualCost || 0),
                        0,
                      ),
                  )}
                </p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">PO Actual Cost</span>
                    <span className="text-gray-300">
                      $
                      {formatNumber(
                        financeData.purchaseOrders.reduce(
                          (sum, po) => sum + (po.actualCost || 0),
                          0,
                        ),
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">WO Subtotal</span>
                    <span className="text-gray-300">
                      ${formatNumber(financeData.totalWorkOrderActualSubtotal)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">WSIB Cost</span>
                    <span className="text-gray-300">
                      ${formatNumber(financeData.totalWsibCost)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Hourly Burden</span>
                    <span className="text-gray-300">
                      ${formatNumber(financeData.totalBurdenCost)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">PW Burden</span>
                    <span className="text-gray-300">
                      ${formatNumber(financeData.totalPieceworkerBurdenCost)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Pending/Forecasted Cost */}
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                  Total Pending Cost
                </p>
                <p className="text-xl font-bold text-orange-400 mb-3">
                  $
                  {formatNumber(
                    financeData.purchaseOrders.reduce(
                      (sum, po) =>
                        sum +
                        (po.actualCost || 0) +
                        (po.pendingMOCost || 0) +
                        (po.pendingBillCost || 0),
                      0,
                    ) +
                      financeData.workOrders.reduce(
                        (sum, wo) => sum + (wo.pendingCost || 0),
                        0,
                      ),
                  )}
                </p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Pending MO</span>
                    <span className="text-gray-300">
                      $
                      {formatNumber(
                        financeData.purchaseOrders.reduce(
                          (sum, po) => sum + (po.pendingMOCost || 0),
                          0,
                        ),
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Pending PO Bills</span>
                    <span className="text-gray-300">
                      $
                      {formatNumber(
                        financeData.purchaseOrders.reduce(
                          (sum, po) => sum + (po.pendingBillCost || 0),
                          0,
                        ),
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Pending WO</span>
                    <span className="text-gray-300">
                      $
                      {formatNumber(
                        financeData.workOrders.reduce(
                          (sum, wo) => sum + (wo.pendingCost || 0),
                          0,
                        ),
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Keep your existing Purchase Orders and Work Orders tables below */}
          {/* Purchase Orders Table */}
          <div className="bg-gray-700 p-5 rounded-lg shadow mb-6">
            <div className="flex items-center justify-between mb-4 border-b border-gray-600 pb-3">
              <h3 className="text-lg font-semibold text-gray-100">
                Purchase Orders
              </h3>
              <span className="text-xs bg-gray-600 text-gray-300 px-2 py-1 rounded-full">
                {financeData.purchaseOrders.length} orders
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-600">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      PO #
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Estimated
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Actual
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Pending MO
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Remaining
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-600/50">
                  {financeData.purchaseOrders.map((po, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-650 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm text-gray-200 font-medium">
                        {po.title}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">
                        {po.poNumber}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300 text-right">
                        ${formatNumber(po.estimatedCost)}
                      </td>
                      <td className="px-4 py-3 text-sm text-emerald-400 text-right font-medium">
                        ${formatNumber(po.actualCost)}
                      </td>
                      <td className="px-4 py-3 text-sm text-orange-400 text-right">
                        ${formatNumber(po.pendingMOCost)}
                      </td>
                      <td
                        className={`px-4 py-3 text-sm text-right font-medium ${
                          po.remainingCost >= 0
                            ? "text-green-400"
                            : "text-red-400"
                        }`}
                      >
                        ${formatNumber(po.remainingCost)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t-2 border-gray-500">
                  <tr className="bg-gray-800/50">
                    <td
                      className="px-4 py-3 text-sm font-semibold text-gray-200"
                      colSpan="2"
                    >
                      Total
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-200 text-right">
                      $
                      {formatNumber(
                        financeData.purchaseOrders.reduce(
                          (sum, po) => sum + (po.estimatedCost || 0),
                          0,
                        ),
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-emerald-400 text-right">
                      $
                      {formatNumber(
                        financeData.purchaseOrders.reduce(
                          (sum, po) => sum + (po.actualCost || 0),
                          0,
                        ),
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-orange-400 text-right">
                      $
                      {formatNumber(
                        financeData.purchaseOrders.reduce(
                          (sum, po) => sum + (po.pendingMOCost || 0),
                          0,
                        ),
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-200 text-right">
                      $
                      {formatNumber(
                        financeData.purchaseOrders.reduce(
                          (sum, po) => sum + (po.remainingCost || 0),
                          0,
                        ),
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Work Orders Table */}
          <div className="bg-gray-700 p-5 rounded-lg shadow">
            <div className="flex items-center justify-between mb-4 border-b border-gray-600 pb-3">
              <h3 className="text-lg font-semibold text-gray-100">
                Work Orders
              </h3>
              <span className="text-xs bg-gray-600 text-gray-300 px-2 py-1 rounded-full">
                {financeData.workOrders.length} orders
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-600">
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      WO #
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Estimated
                    </th>
                    <th
                      className="px-3 py-3 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider bg-gray-600/30"
                      colSpan="5"
                    >
                      <span className="text-emerald-400">Actual Breakdown</span>
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-orange-400 uppercase tracking-wider">
                      Pending
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Remaining
                    </th>
                  </tr>
                  <tr className="border-b border-gray-600/50 bg-gray-800/30">
                    <th className="px-3 py-2"></th>
                    <th className="px-3 py-2"></th>
                    <th className="px-3 py-2"></th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500 font-normal">
                      Subtotal
                    </th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500 font-normal">
                      WSIB
                    </th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500 font-normal">
                      Hourly Burden
                    </th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500 font-normal">
                      PW Burden
                    </th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500 font-normal">
                      Total
                    </th>
                    <th className="px-3 py-2"></th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-600/50">
                  {financeData.workOrders.map((wo, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-650 transition-colors"
                    >
                      <td className="px-3 py-3 text-sm text-gray-200 font-medium">
                        {wo.title}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-400">
                        {wo.woNumber}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-300 text-right">
                        ${formatNumber(wo.estimatedCost)}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-300 text-right bg-gray-600/10">
                        ${formatNumber(wo.actualCostBreakdown.subtotal)}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-300 text-right bg-gray-600/10">
                        ${formatNumber(wo.actualCostBreakdown.wsibCost)}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-300 text-right bg-gray-600/10">
                        ${formatNumber(wo.actualCostBreakdown.burdenCost)}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-300 text-right bg-gray-600/10">
                        $
                        {formatNumber(
                          wo.actualCostBreakdown.pieceworkerBurdenCost,
                        )}
                      </td>
                      <td className="px-3 py-3 text-sm text-emerald-400 text-right font-medium bg-gray-600/10">
                        ${formatNumber(wo.actualCost)}
                      </td>
                      <td className="px-3 py-3 text-sm text-orange-400 text-right">
                        ${formatNumber(wo.pendingCost)}
                      </td>
                      <td
                        className={`px-3 py-3 text-sm text-right font-medium ${
                          wo.remainingCost >= 0
                            ? "text-green-400"
                            : "text-red-400"
                        }`}
                      >
                        ${formatNumber(wo.remainingCost)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t-2 border-gray-500">
                  <tr className="bg-gray-800/50">
                    <td
                      className="px-3 py-3 text-sm font-semibold text-gray-200"
                      colSpan="2"
                    >
                      Total
                    </td>
                    <td className="px-3 py-3 text-sm font-semibold text-gray-200 text-right">
                      $
                      {formatNumber(
                        financeData.workOrders.reduce(
                          (sum, wo) => sum + (wo.estimatedCost || 0),
                          0,
                        ),
                      )}
                    </td>
                    <td className="px-3 py-3 text-sm font-semibold text-gray-300 text-right bg-gray-600/10">
                      ${formatNumber(financeData.totalWorkOrderActualSubtotal)}
                    </td>
                    <td className="px-3 py-3 text-sm font-semibold text-gray-300 text-right bg-gray-600/10">
                      ${formatNumber(financeData.totalWsibCost)}
                    </td>
                    <td className="px-3 py-3 text-sm font-semibold text-gray-300 text-right bg-gray-600/10">
                      ${formatNumber(financeData.totalBurdenCost)}
                    </td>
                    <td className="px-3 py-3 text-sm font-semibold text-gray-300 text-right bg-gray-600/10">
                      ${formatNumber(financeData.totalPieceworkerBurdenCost)}
                    </td>
                    <td className="px-3 py-3 text-sm font-semibold text-emerald-400 text-right bg-gray-600/10">
                      $
                      {formatNumber(
                        financeData.workOrders.reduce(
                          (sum, wo) => sum + (wo.actualCost || 0),
                          0,
                        ),
                      )}
                    </td>
                    <td className="px-3 py-3 text-sm font-semibold text-orange-400 text-right">
                      $
                      {formatNumber(
                        financeData.workOrders.reduce(
                          (sum, wo) => sum + (wo.pendingCost || 0),
                          0,
                        ),
                      )}
                    </td>
                    <td className="px-3 py-3 text-sm font-semibold text-gray-200 text-right">
                      $
                      {formatNumber(
                        financeData.workOrders.reduce(
                          (sum, wo) => sum + (wo.remainingCost || 0),
                          0,
                        ),
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricsTab;
